import { ArticlePreviewResponse, ContentType, ProfileUserResponse, PublicCollectionResponse, UserResponse } from "@authory-packages/core";
import { APPLICATION_ROUTES } from "../shared-logic/routes";
import { getIdentityLinkUrls } from "./profileIdentityLinks";
import { getDisplayName } from "./getDisplayName";

type JsonLdValue = string | number | JsonLdNode | JsonLdValue[] | undefined;

export interface JsonLdNode {
    [key: string]: JsonLdValue;
}

export type StructuredDataItem = Pick<
    ArticlePreviewResponse,
    | "type"
    | "title"
    | "ogTitle"
    | "description"
    | "ogDescription"
    | "date"
    | "originalUrl"
    | "sourceName"
    | "previewImage"
    | "thumbnailImage"
    | "publishedOnAuthory"
    | "canonicalSlug"
    | "seoHidden"
    | "owner"
>;

export type StructuredDataCollection = Pick<PublicCollectionResponse, "label" | "description" | "canonicalSlug" | "articleCount">;

export interface StructuredDataPage {
    user: UserResponse | ProfileUserResponse;
    // The canonical url, which for a crawled item is the original article, and the page's own address
    pageUrl: string;
    selfUrl: string;
    // The portfolio root on the host serving the page, the custom domain when there is one
    homeUrl: string;
    isMainDomain: boolean;
    title: string;
}

type ItemSchemaType =
    "Article" | "SocialMediaPosting" | "PodcastEpisode" | "AudioObject" | "VideoObject" | "DigitalDocument" | "ImageObject" | "Book" | "WebPage";

const ITEM_SCHEMA_TYPES: Record<ContentType, ItemSchemaType> = {
    [ContentType.Article]: "Article",
    [ContentType.Email]: "Article",
    [ContentType.TextPost]: "SocialMediaPosting",
    [ContentType.ImagePost]: "SocialMediaPosting",
    [ContentType.Thread]: "SocialMediaPosting",
    [ContentType.VideoPost]: "SocialMediaPosting",
    [ContentType.ReplyPost]: "SocialMediaPosting",
    [ContentType.PodcastItem]: "PodcastEpisode",
    [ContentType.Audio]: "AudioObject",
    [ContentType.Video]: "VideoObject",
    [ContentType.PdfFile]: "DigitalDocument",
    [ContentType.Image]: "ImageObject",
    [ContentType.Book]: "Book",
    [ContentType.Website]: "WebPage"
};

// Podcasts, audio, video and images often credit hosts, guests or crews, so the owner is never claimed as their author
const AUTHORED_SCHEMA_TYPES = new Set<ItemSchemaType>(["Article", "SocialMediaPosting", "DigitalDocument", "Book"]);

/**
 * Builds the identifier of the person behind a portfolio. Always on the main domain so it is the same on every host.
 * @param slug The portfolio owner's slug
 * @returns The Person @id
 */
export function getPersonId(slug: string) {
    return `${process.env.NEXT_PUBLIC_FRONTEND_URL}/${slug}#person`;
}

/**
 * Builds the graph for the portfolio page, listing the items and collections a visitor sees on it.
 * @param page The page being rendered
 * @param items The items listed on the page
 * @param collections The collections the page links to as tabs or cards
 * @returns The JSON-LD document
 */
export function buildProfilePageStructuredData(page: StructuredDataPage, items: StructuredDataItem[], collections: StructuredDataCollection[]) {
    const itemNodes = items
        .map((item) => buildItemNode(item, page.user, getItemCanonicalUrl(item, page.homeUrl)))
        .filter(isJsonLdNode)
        .filter((node, index, nodes) => nodes.findIndex((other) => other.url === node.url) === index);

    const collectionNodes = collections.map((collection) => buildCollectionNode(collection, getProfileCollectionUrl(collection, page.homeUrl)));

    const hasPart = [...itemNodes, ...collectionNodes];

    return buildGraph([
        {
            ...getProfilePageRef(page),
            "@type": "ProfilePage",
            url: page.pageUrl,
            mainEntity: getPersonRef(page.user),
            isPartOf: getWebSiteRef(page),
            hasPart: hasPart.length ? hasPart : undefined
        },
        buildPersonNode(page),
        buildWebSiteNode(page)
    ]);
}

/**
 * Builds the graph for one of the owner's about pages, a part of the portfolio about its owner.
 * @param page The page being rendered
 * @returns The JSON-LD document
 */
export function buildAboutPageStructuredData(page: StructuredDataPage) {
    const nodes: JsonLdNode[] = [
        {
            "@type": "AboutPage",
            url: page.pageUrl,
            name: page.title || undefined,
            mainEntity: getPersonRef(page.user),
            isPartOf: getProfilePageRef(page)
        },
        buildPersonNode(page),
        buildWebSiteNode(page)
    ];

    if (page.title) nodes.push(buildBreadcrumbNode(page));

    return buildGraph(nodes);
}

/**
 * Builds the graph for a collection page.
 * @param page The page being rendered
 * @param collection The collection shown on the page
 * @param isInProfile Whether the page sits under the portfolio, which earns it a breadcrumb
 * @returns The JSON-LD document
 */
export function buildCollectionPageStructuredData(page: StructuredDataPage, collection: StructuredDataCollection, isInProfile: boolean) {
    const nodes: JsonLdNode[] = [
        {
            ...buildCollectionNode(collection, page.pageUrl),
            author: getPersonRef(page.user),
            isPartOf: getWebSiteRef(page)
        },
        buildPersonNode(page),
        buildWebSiteNode(page)
    ];

    if (isInProfile) nodes.push(buildBreadcrumbNode(page));

    return buildGraph(nodes);
}

/**
 * Builds the graph for a content item page: a record of the work, whose main entity is the item at its canonical address.
 * @param page The page being rendered
 * @param item The item shown on the page
 * @param isInProfile Whether the page sits under the portfolio, which makes it part of the portfolio with a breadcrumb
 * @returns The JSON-LD document
 */
export function buildContentItemStructuredData(page: StructuredDataPage, item: StructuredDataItem, isInProfile: boolean) {
    const itemNode = buildItemNode(item, page.user, page.pageUrl);
    const excerpt = (item.ogDescription || item.description || "").trim() || undefined;

    const nodes: JsonLdNode[] = [
        {
            "@type": "WebPage",
            url: page.selfUrl,
            name: page.title || undefined,
            // A post already carries its excerpt as text
            mainEntity: itemNode ? { ...itemNode, description: itemNode.text ? undefined : excerpt } : undefined,
            isPartOf: isInProfile ? getProfilePageRef(page) : getWebSiteRef(page)
        },
        buildPersonNode(page),
        buildWebSiteNode(page)
    ];

    if (isInProfile && page.title) nodes.push(buildBreadcrumbNode(page));

    return buildGraph(nodes);
}

/**
 * Wraps nodes in a JSON-LD document.
 * @param nodes The graph nodes
 * @returns The JSON-LD document
 */
function buildGraph(nodes: JsonLdNode[]): JsonLdNode {
    return { "@context": "https://schema.org", "@graph": nodes };
}

/**
 * Builds the Person node, identical on every page of the portfolio.
 * @param page The page being rendered
 * @returns The Person node
 */
function buildPersonNode({ user, homeUrl }: StructuredDataPage): JsonLdNode {
    const sameAs = getIdentityLinkUrls(user.profileComponents);

    return {
        "@type": "Person",
        "@id": getPersonId(user.slug),
        name: getDisplayName(user).trim(),
        description: user.bio?.trim() || undefined,
        image: user.avatarUrl?.[0]?.url || undefined,
        url: homeUrl,
        mainEntityOfPage: homeUrl,
        sameAs: sameAs.length ? sameAs : undefined
    };
}

/**
 * Builds the WebSite node, Authory on the main domain, the owner's own site on a custom domain.
 * @param page The page being rendered
 * @returns The WebSite node
 */
function buildWebSiteNode(page: StructuredDataPage): JsonLdNode {
    if (page.isMainDomain) {
        return { ...getWebSiteRef(page), "@type": "WebSite", name: "Authory", url: process.env.NEXT_PUBLIC_FRONTEND_URL };
    }

    return {
        ...getWebSiteRef(page),
        "@type": "WebSite",
        name: getDisplayName(page.user).trim(),
        url: page.homeUrl,
        author: getPersonRef(page.user)
    };
}

/**
 * Builds the two step trail from the portfolio to the current page.
 * @param page The page being rendered
 * @returns The BreadcrumbList node
 */
function buildBreadcrumbNode(page: StructuredDataPage): JsonLdNode {
    return {
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: getDisplayName(page.user).trim(), item: page.homeUrl },
            { "@type": "ListItem", position: 2, name: page.title, item: page.selfUrl }
        ]
    };
}

/**
 * Builds a CollectionPage node. The item count lives on an ItemList, CollectionPage has no numberOfItems.
 * @param collection The collection
 * @param url The collection page url
 * @returns The CollectionPage node
 */
function buildCollectionNode(collection: StructuredDataCollection, url: string): JsonLdNode {
    return {
        "@type": "CollectionPage",
        name: collection.label || undefined,
        description: collection.description || undefined,
        url,
        mainEntity: { "@type": "ItemList", numberOfItems: collection.articleCount }
    };
}

/**
 * Builds the node for a content item, typed by its content type.
 * @param item The content item
 * @param user The portfolio owner
 * @param url The item's canonical url
 * @returns The node, or null when the item is hidden from search or lacks the fields its type requires
 */
function buildItemNode(item: StructuredDataItem, user: UserResponse | ProfileUserResponse, url: string): JsonLdNode | null {
    if (item.seoHidden || !url) return null;

    const schemaType = ITEM_SCHEMA_TYPES[item.type];
    const name = (item.ogTitle || item.title || "").trim() || undefined;
    const image = item.previewImage || item.thumbnailImage || undefined;
    const date = item.date || undefined;
    const publisher = getPublisherNode(item);
    // Items of a business team member list on the parent portfolio, they are not the owner's work
    const author = AUTHORED_SCHEMA_TYPES.has(schemaType) && item.owner?.slug === user.slug ? getPersonRef(user) : undefined;

    switch (schemaType) {
        case "VideoObject":
            if (!name || !image || !date) return null;
            return { "@type": schemaType, name, url, thumbnailUrl: image, uploadDate: date, publisher };
        case "ImageObject":
            if (!image) return null;
            return { "@type": schemaType, name, url, contentUrl: image, datePublished: date };
        case "SocialMediaPosting": {
            const text = (item.description || "").trim() || undefined;
            if (!name && !text) return null;
            return { "@type": schemaType, headline: name, text, url, datePublished: date, image, author, publisher };
        }
        case "PodcastEpisode":
            if (!name) return null;
            return {
                "@type": schemaType,
                name,
                url,
                datePublished: date,
                image,
                partOfSeries: item.sourceName ? { "@type": "PodcastSeries", name: item.sourceName } : undefined
            };
        case "Article":
            if (!name) return null;
            return { "@type": schemaType, headline: name, url, datePublished: date, image, author, publisher };
        default:
            if (!name) return null;
            return { "@type": schemaType, name, url, datePublished: date, image, author, publisher };
    }
}

/**
 * Builds the outlet an item was published in, from its source name and original domain.
 * @param item The content item
 * @returns The Organization node, or undefined for items hosted on Authory or without a usable original url
 */
function getPublisherNode(item: StructuredDataItem): JsonLdNode | undefined {
    if (item.publishedOnAuthory || !item.sourceName || !item.originalUrl) return undefined;

    try {
        return { "@type": "Organization", name: item.sourceName, url: new URL(item.originalUrl).origin };
    } catch {
        return undefined;
    }
}

/**
 * Resolves the canonical url of a listed item, the original for crawled items, the Authory page for hosted ones.
 * @param item The content item
 * @param homeUrl The portfolio root
 * @returns The url, or an empty string when none can be derived
 */
function getItemCanonicalUrl(item: StructuredDataItem, homeUrl: string) {
    if (!item.publishedOnAuthory) return item.originalUrl || "";
    if (!item.canonicalSlug) return "";

    return `${homeUrl}${APPLICATION_ROUTES.PROFILE_CONTENT_ITEM_PAGE.replace("/[author]", "").replace("[contentItemAccessKey]", item.canonicalSlug)}`;
}

/**
 * Resolves the url of a collection under the portfolio.
 * @param collection The collection
 * @param homeUrl The portfolio root
 * @returns The collection page url
 */
function getProfileCollectionUrl(collection: StructuredDataCollection, homeUrl: string) {
    return `${homeUrl}${APPLICATION_ROUTES.PROFILE_COLLECTION_PAGE.replace("/[author]", "").replace("[collectionAccessKey]", collection.canonicalSlug)}`;
}

/**
 * References the owner's Person node.
 * @param user The portfolio owner
 * @returns The @id reference
 */
function getPersonRef(user: UserResponse | ProfileUserResponse): JsonLdNode {
    return { "@id": getPersonId(user.slug) };
}

/**
 * References the portfolio's ProfilePage, identified by the portfolio url on the host serving the page.
 * @param page The page being rendered
 * @returns The @id reference
 */
function getProfilePageRef(page: StructuredDataPage): JsonLdNode {
    return { "@id": page.homeUrl };
}

/**
 * References the WebSite node of the host serving the page.
 * @param page The page being rendered
 * @returns The @id reference
 */
function getWebSiteRef(page: StructuredDataPage): JsonLdNode {
    const siteUrl = page.isMainDomain ? process.env.NEXT_PUBLIC_FRONTEND_URL : page.homeUrl;

    return { "@id": `${siteUrl}/#website` };
}

/**
 * Narrows out items that produced no node.
 * @param node The node or null
 * @returns True when there is a node
 */
function isJsonLdNode(node: JsonLdNode | null): node is JsonLdNode {
    return node !== null;
}
